import AtriumApi from "api";
import DarkButton from "components/actions/DarkButton";
import GrayButton from "components/actions/GrayButton";
import AlertPane from "components/alert";
import DatePicker from "components/fields/DatePicker";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import { useAuth } from "hooks/useAuth";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { RiDeleteBinLine } from "react-icons/ri";
import ConfirmationDialog from "./confirmation-dialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import MapboxAddressInput from "components/mapbox";
import dayjs from "dayjs";

const UserForm = (props) => {
  const { onClose } = props;

  const auth = useAuth();
  const [user, setUser] = useState(
    {
      role: props?.user?.role || "",
      status: props?.user?.status || "",
      campus: props?.user?.campus || "",
      firstName: props?.user?.firstName || "",
      lastName: props?.user?.lastName || "",
      email: props?.user?.email || "",
      phone: props?.user?.phone || "",
      dob: props?.user?.dob || "",
      familyRelationship: props?.user?.familyRelationship || "",
      gender: props?.user?.gender || "",
      avatar: props?.user?.avatar?.url || "",
      address: props?.user?.address || "",
      city: props?.user?.city || "",
      street: props?.user?.street || "",
      state: props?.user?.state || "",
      zip: props?.user?.zip || "",
      country: props?.user?.country || "",
      id: props?.user?.id || "",
    } || {}
  );

  const [errors, setErrors] = useState([]);
  const [notice, setNotice] = useState("");
  const [image, setImage] = useState(props.user?.avatar?.url || "");
  const [imageFile, setImageFile] = useState(null); // <-- Track the actual File

  const [location, setLocation] = useState(
    {
      type: "Point",
      coordinates: [props?.user?.lng, props?.user?.lat],
    } || {}
  );

  const [campuses, setCampuses] = useState([]);

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    message: "",
    onConfirm: () => {},
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImage(imageUrl);
      setImageFile(file); // <-- Store the file
    }
  };

  useEffect(() => {
    const fetchAllCampuses = async () => {
      try {
        const response = await AtriumApi.get("/v2/campuses/allCampuses");
        if (response) {
          setCampuses(response?.data?.data?.campuses?.data);
        }
      } catch (error) {
        console.log("fetching all campuses error", error);
      }
    };
    fetchAllCampuses();
  }, []);

  useEffect(() => {
    if (!user.id && props.user?.id) {
      setUser({
        role: props?.user?.role,
        status: props?.user?.status,
        campus: props?.user?.campus,
        firstName: props?.user?.firstName,
        lastName: props?.user?.lastName,
        email: props?.user?.email,
        phone: props?.user?.phone,
        dob: props?.user?.dob,
        familyRelationship: props?.user?.familyRelationship,
        gender: props?.user?.gender,
        avatar: props?.user?.avatar?.url,
        address: props?.user?.address,
        city: props?.user?.city,
        street: props?.user?.street,
        state: props?.user?.state,
        zip: props?.user?.zip,
        country: props?.user?.country,
        id: props?.user?.id,
      });
      setImage(props?.user?.avatar?.url);
      setLocation({
        type: "Point",
        coordinates: [props?.user?.lng, props?.user?.lat],
      });
    }
  }, [user, props.user]);

  const getValidationSchema = (isEdit) =>
    Yup.object().shape({
      role: Yup.string().required("Role is required"),
      status: Yup.string().required("Status is required"),
      campus: Yup.string().required("Campus is required"),
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      // phone: Yup.string()
      //   .matches(/^[0-9\s\-()+]*$/, "Phone number should only contain numbers.")
      //   .required("Phone number is required"),
      phone: Yup.string()
        .matches(/^[0-9\s\-()+]*$/, "Phone number should only contain numbers.")
        .nullable()
        .when([], {
          is: () => !isEdit,
          then: (schema) => schema.required("Phone number is required"),
          otherwise: (schema) => schema.notRequired(),
        }),

      // dob: Yup.string().required("Date of birth is required"),
      familyRelationship: Yup.string().required(
        "Family relationship is required"
      ),
      gender: Yup.string().required("Gender is required"),
      address: Yup.string().required("Address is required"),
    });

  const onSubmit = (values) => {
    const formElement = document.querySelector("form");
    formElement.classList.add("animate-pulse");

    const submitButton = formElement.querySelector('button[type="submit"]');
    submitButton.setAttribute("disabled", "");

    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      if (key !== "avatar") {
        formData.append(key, values[key]);
      }
      if (key === "dob") {
        console.log("dob", values[key]);
      }
    });

    formData.append("location", JSON.stringify(location));

    if (imageFile) {
      formData.append("avatar", imageFile);
    } else {
      formData.append("avatar", image);
    }

    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    const request = user?.id
      ? AtriumApi.put(`/v1/users/${user?.id}`, formData, config)
      : AtriumApi.post("/v1/users", formData, config);

    request
      .then((response) => {
        formElement.classList.remove("animate-pulse");
        submitButton.removeAttribute("disabled");

        if (response.status === 422) {
          toast.error(response.data.message);
        } else if (response.status === 200) {
          toast.success(
            `User ${user.id ? "updated" : "created"} successfully.`
          );
          onClose();
        } else {
          throw new Error("unexpected response");
        }
      })
      .catch((error) => {
        toast.error(error?.data?.message || "Something went wrong");
        formElement.classList.remove("animate-pulse");
        submitButton.removeAttribute("disabled");
        setErrors([
          { path: "general", message: "An error occurred while saving" },
        ]);
      });
  };

  const handleDelete = () => {
    setConfirmationDialog({
      open: true,
      message: "You want to delete this user?",
      onConfirm: async () => {
        try {
          await AtriumApi.delete(`/v1/deleteUser/${user?.id}`).then(() => {
            toast.success("User deleted successfully.");
            onClose();
          });
        } catch (err) {
          toast.error(err?.data?.message || "An error occurred");
        } finally {
          setConfirmationDialog({
            open: false,
            message: "",
            onConfirm: () => {},
          });
        }
      },
    });
  };

  // MM/DD/YYYY format
  function formatToMMDDYYYY(date) {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={user || {}}
        // validationSchema={validationSchema}
        validationSchema={getValidationSchema(!!user?.id)}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form
            onSubmit={(e) => {
              console.log("values", values);
              e.preventDefault(); // Stop default form behavior
              handleSubmit(); // Call Formik's submit
            }}
            noValidate
          >
            {notice && <AlertPane state="success">{notice}</AlertPane>}
            <p className="font-bold text-gray-800 text-base pt-6 dark:text-white">
              Settings
            </p>
            <div className="flex gap-4 pt-4">
              {/* <div className="flex-1 flex flex-col">
          <label
            className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
            htmlFor="role"
          >
            Role
          </label>
          <Select
            id="role"
            value={user.role || ""}
            onChange={(e) => setUser({ ...user, role: e.target.value })}
            required
          >
            <option value="" disabled hidden>
              Member
            </option>
            {auth.user?.role === "admin" && (
              <>
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
              </>
            )}
            <option value="" disabled hidden>
              Select a role
            </option>
            <option value="volunteer">Volunteer</option>
            <option value="member">Member</option>
          </Select>
        </div> */}
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="role"
                >
                  Role
                </label>
                <Select
                  name="role"
                  value={values.role || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  style={{
                    borderColor: touched.role && errors.role ? "red" : "",
                  }}
                >
                  <option value="" disabled hidden>
                    Select a role
                  </option>
                  <option value="volunteer">Volunteer</option>
                  <option value="member">Member</option>
                </Select>
                {touched.role && errors.role && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.role}
                  </p>
                )}
              </div>

              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="status"
                >
                  Status
                </label>
                <Select
                  name="status"
                  value={values.status || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{
                    borderColor: touched.status && errors.status ? "red" : "",
                  }}
                  required
                >
                  <option value="" disabled hidden>
                    Select status
                  </option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
                {touched.status && errors.status && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.status}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col pt-4">
              <label
                className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                htmlFor="campus"
              >
                Campus
              </label>
              <Select
                name="campus"
                value={values.campus || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                style={{
                  borderColor: touched.campus && errors.campus ? "red" : "",
                }}
              >
                <option value="" disabled hidden>
                  Select a campus
                </option>
                {campuses?.length > 0 ? (
                  campuses.map((campus) => (
                    <option key={campus?.id} value={campus?.id}>
                      {campus?.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No campuses found
                  </option>
                )}
              </Select>
              {touched.campus && errors.campus && (
                <p className="text-red-500 text-xs mt-1 ml-2">
                  {errors.campus}
                </p>
              )}
            </div>

            <p className="font-bold text-gray-800 text-base py-6 dark:text-white">
              User details
            </p>
            <div className="flex items-center space-x-4 mb-3">
              {/* Profile Picture */}
              {image ? (
                <img
                  src={image}
                  alt="Profile"
                  className="w-14 h-14 rounded-full object-cover"
                />
              ) : (
                <div className="h-[64px] w-[64px] rounded-full bg-gray-300 flex items-center justify-center text-sm text-gray-500">
                  No Image
                </div>
              )}

              {/* Upload Button */}
              <div>
                {/* <DarkButton
              size="sm"
              htmlFor="upload"
            >
              Upload picture
            </DarkButton> */}
                <label
                  htmlFor="upload"
                  className="cursor-pointer text-[12px] px-3 py-2 text-white bg-blue-800 rounded-xl hover:bg-blue-800 transition"
                >
                  Upload picture
                </label>
                <input
                  id="upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              {/* First Name Field */}
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="gender"
                >
                  First Name
                </label>
                <InputField
                  variant="user_form"
                  // label="First name"
                  placeholder=""
                  name="firstName"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.firstName}
                  touched={touched.firstName}
                />
                {/* {touched.firstName &&
                  errors.find((e) => e.path === "firstName") && (
                    <p className="text-red-500 text-xs mt-1 ml-2">
                      {errors.find((e) => e.path === "firstName").message}
                    </p>
                  )} */}
              </div>
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="gender"
                >
                  Last Name
                </label>
                <InputField
                  variant="user_form"
                  // label="Last name"
                  placeholder=""
                  name="lastName"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.lastName}
                  touched={touched.lastName}
                />
                {/* {touched.lastName &&
                  errors.find((e) => e.path === "lastName") && (
                    <p className="text-red-500 text-xs mt-1 ml-2">
                      {errors.find((e) => e.path === "lastName").message}
                    </p>
                  )} */}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              {/* Email Field */}
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="gender"
                >
                  Email Address
                </label>
                <InputField
                  variant="user_form"
                  // label="Contact email"
                  placeholder=""
                  name="email"
                  type="email"
                  extra="flex-1 flex flex-col"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.email}
                  touched={touched.email}
                />
                {/* {touched.email && errors.find((e) => e.path === "email") && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.find((e) => e.path === "email").message}
                  </p>
                )} */}
              </div>
              <div className="text-sm text-navy-700 flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="gender"
                >
                  Contact Phone
                </label>
                <InputField
                  variant="user_form"
                  // label="Contact phone"
                  placeholder=""
                  name="phone"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.phone}
                  touched={touched.phone}
                />
                {/* {touched.phone && errors.find((e) => e.path === "phone") && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.find((e) => e.path === "phone").message}
                  </p>
                )} */}
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <div className="flex-1 flex flex-col">
                <DatePicker
                  name="dob"
                  isFutureDateDisabled
                  label="Date of birth"
                  height={30}
                  // state={
                  //   !values.dob && touched.dob && errors.dob !== -1
                  //     ? "error"
                  //     : ""
                  // }
                  // value={
                  //   formatToMMDDYYYY(
                  //     values.dob && new Date(values.dob).toLocaleDateString()
                  //   ) || ""
                  // }
                  // onChange={(v) =>
                  //   setFieldValue("dob", v.toLocaleDateString())
                  // }
                  value={formatToMMDDYYYY(values.dob)} // ✅ Correct
                  // onChange={(v) => setFieldValue("dob", v)}
                  onChange={(v) => {
                    const formatted = dayjs(v).format("YYYY-MM-DD");
                    setFieldValue("dob", formatted);
                  }}
                  onBlur={handleBlur}
                />
                {/* {touched.dob && errors.dob && (
                  <p className="text-red-500 text-xs mt-1 ml-2">{errors.dob}</p>
                )} */}
              </div>
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="family-relationship"
                >
                  Family relationship
                </label>
                <Select
                  name="familyRelationship"
                  value={values.familyRelationship}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.familyRelationship}
                  touched={touched.familyRelationship}
                  style={{
                    borderColor:
                      touched.familyRelationship && errors.familyRelationship
                        ? "red"
                        : "",
                  }}
                >
                  <option value="" disabled hidden></option>
                  <option value="primary">Primary</option>
                  <option value="married">Married</option>
                  <option value="child">Child</option>
                  <option value="unknown">Unknown</option>
                </Select>
                {touched.familyRelationship && errors.familyRelationship && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.familyRelationship}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="gender"
                >
                  Gender
                </label>
                <Select
                  name="gender"
                  value={values.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.gender}
                  touched={touched.gender}
                  style={{
                    borderColor: touched.gender && errors.gender ? "red" : "",
                  }}
                  required
                >
                  <option value="" disabled hidden>
                    Select gender
                  </option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  {/* <option value="other">Non-binary</option>
            <option value="na">Prefer not to tell</option> */}
                </Select>
                {touched.gender && errors.gender && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.gender}
                  </p>
                )}
              </div>
              <div className="flex-1"></div>
            </div>

            <p className="font-bold text-gray-800 text-base pt-6 dark:text-white">
              Location
            </p>
            <div>
              <MapboxAddressInput
                label="Address"
                id="address"
                name="address"
                value={values?.address || props.user?.address || ""}
                onResult={(data) => {
                  setLocation({
                    type: "Point",
                    coordinates: [data?.lng, data?.lat],
                  });

                  if (data) {
                    setFieldValue("address", data?.address || "");
                    setFieldValue("state", data?.state || "");
                    setFieldValue("city", data?.city || "");
                    setFieldValue(
                      "street",
                      data?.street || data?.address || ""
                    );
                    setFieldValue("zip", data?.zipCode || "");
                  }
                }}
                onClearInput={() => {
                  // Clear all related address fields
                  setLocation({}); // or set it to default if needed
                  setFieldValue("address", "");
                  setFieldValue("state", "");
                  setFieldValue("city", "");
                  setFieldValue("street", "");
                  setFieldValue("zip", "");
                }}
              />
              {!values.address && touched.address && errors.address && (
                <p className="text-red-500 text-xs mt-1 ml-2">
                  {errors.address}
                </p>
              )}
            </div>
            <div className="flex gap-4 pt-4">
              <InputField
                variant="user_form"
                label="State"
                placeholder=""
                name="state"
                type="text"
                extra="flex-1 flex flex-col"
                value={values.state}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.state}
                touched={touched.state}
              />
              <InputField
                variant="user_form"
                label="City"
                placeholder=""
                name="city"
                type="text"
                extra="flex-1 flex flex-col"
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.city}
                touched={touched.city}
              />
            </div>
            <div className="flex gap-4 pt-4">
              <InputField
                variant="user_form"
                label="Street address"
                placeholder=""
                name="street"
                type="text"
                extra="flex-1 flex flex-col"
                value={values.street}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.street}
                touched={touched.street}
              />
              <InputField
                variant="user_form"
                label="Zip code"
                placeholder=""
                name="zip"
                type="text"
                extra="flex-1 flex flex-col"
                value={values.zip}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.zip}
                touched={touched.zip}
              />
            </div>

            <div className="flex gap-4 py-6">
              <DarkButton size={"lg"} type="submit">
                {user.id ? "Save changes" : "Create user"}
              </DarkButton>
              {user.id && (
                <DarkButton
                  onClick={handleDelete}
                  type="button"
                  size={"lg"}
                  kind="danger"
                >
                  <span className="flex items-center">
                    {" "}
                    <RiDeleteBinLine className="mr-2" /> Delete{" "}
                  </span>
                </DarkButton>
              )}

              <GrayButton
                size={"lg"}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
              >
                Cancel
              </GrayButton>
            </div>
          </Form>
        )}
      </Formik>

      <ConfirmationDialog
        isOpen={confirmationDialog.open}
        message={confirmationDialog.message}
        onClose={() => setConfirmationDialog({ open: false, message: "" })}
        onConfirm={confirmationDialog.onConfirm}
      />
    </>
  );
};

export default UserForm;
